import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { packageButtonStyles } from "../../styles";
import type { OpenModalEvent } from "../../events";
import type { Package } from "./entities";

@customElement("umb-market-retired-package-toast")
export class UmbMarketRetiredPackagToast extends LitElement {
  @property({ type: Object })
  package!: Package;

  async #retiredPackageLinkClick(e: PointerEvent) {
    e.preventDefault();
    if (!this.package.alternatePackageIdForRetiredPackage) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent<OpenModalEvent>("open-modal", {
        bubbles: true,
        composed: true,
        detail: {
          packageId: this.package.alternatePackageIdForRetiredPackage,
          direct: true,
          iconDominantColor: this.package.iconDominantColor,
        },
      })
    );
  }

  #getRetiredMessage() {
    if (!this.package.isRetired) {
      return null;
    }

    // build the deprecation message
    // use the provided text if it exists, then link to either the marketplace
    // package or nuget source if the package doesn't exist in our repo

    const retiredReason =
      this.package.retiredReason ??
      "This package has been deprecated and is no longer maintained.";

    if (!this.package.alternatePackageIdForRetiredPackage) {
      return html`<p style="margin: 0;">${retiredReason}</p>`;
    }

    const alternatePackageLink = this.package
      .alternatePackageIdForRetiredPackageExistsInCatalog
      ? `/package/${this.package.alternatePackageIdForRetiredPackage?.toLowerCase()}`
      : `https://nuget.org/packages/${this.package.alternatePackageIdForRetiredPackage}`;

    return html`<div class="alert">
      <p>${retiredReason}</p>
      <umb-market-button
        @click=${this.#retiredPackageLinkClick}
        href=${alternatePackageLink}
        target=${this.package.alternatePackageIdForRetiredPackageExistsInCatalog
          ? "_self"
          : "_blank"}
      >
        <div class="btn-text">
          <small class="btn-subtitle">Suggested alternative</small>
          <span class="btn-title"
            >${this.package.alternatePackageIdForRetiredPackage}</span
          >
        </div></umb-market-button
      >
    </div>`;
  }

  render() {
    return html`${this.#getRetiredMessage()}`;
  }

  static styles = [
    packageButtonStyles,
    css`
      :host {
        display: block;
        width:100%;
      }

      .alert {
        display: flex;
        flex-direction: column;
        border: 2px solid var(--uui-color-current-contrast);
        padding: var(--xs-spacer);
        border-radius: var(--xs-spacer);
        color: var(--uui-color-surface);
        background: var(--uui-color-current-contrast);
      }

      umb-market-button {
        --button-background: var(--uui-color-surface);
        --button-color: var(--uui-color-current-contrast);
        --button-width: auto;
        display: block;
        margin: var(--xs-spacer) 0 0;
        max-width: 100%;
      }

      .alert p,
      umb-market-button small {
        margin: 0;
      }
    `,
  ];
}
